import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Medal, Crown, Home, RotateCcw } from 'lucide-react';

const RANK_STYLES = [
  { bg: 'from-yellow-500/20 to-amber-500/20', border: 'border-yellow-400', icon: Trophy, iconColor: 'text-yellow-400' },
  { bg: 'from-slate-300/20 to-slate-400/20', border: 'border-slate-300', icon: Medal, iconColor: 'text-slate-300' },
  { bg: 'from-orange-600/20 to-amber-700/20', border: 'border-orange-500', icon: Medal, iconColor: 'text-orange-400' },
];

const MultiGameOver = ({ multiState }) => {
  const navigate = useNavigate();

  if (!multiState) return null;

  const { rankings = [], winner, your_country } = multiState;
  // Identifier le joueur courant par son pays
  const currentUsername = rankings.find(r => r.country_short_name === your_country)?.username;
  const isWinner = winner === currentUsername;

  return (
    <div className="min-h-screen bg-black relative overflow-hidden flex items-center justify-center">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute inset-0 bg-gradient-to-br ${
          isWinner
            ? 'from-yellow-900/30 via-amber-900/20 to-black'
            : 'from-red-900/20 via-purple-900/20 to-black'
        }`} />
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8 w-full">
        {/* Result header */}
        <div className="text-center mb-8">
          {isWinner ? (
            <>
              <Crown className="w-20 h-20 text-yellow-400 mx-auto mb-4 animate-bounce" />
              <h1 className="text-5xl font-black bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 bg-clip-text text-transparent mb-2">
                VICTOIRE !
              </h1>
            </>
          ) : (
            <>
              <Medal className="w-20 h-20 text-slate-400 mx-auto mb-4" />
              <h1 className="text-5xl font-black bg-gradient-to-r from-slate-300 via-blue-300 to-purple-300 bg-clip-text text-transparent mb-2">
                PARTIE TERMINEE
              </h1>
            </>
          )}
          <p className="text-slate-400">
            {winner ? `${winner} remporte la partie !` : 'La partie est terminée.'}
          </p>
        </div>

        {/* Rankings */}
        <div className="p-6 rounded-2xl bg-slate-900/50 backdrop-blur-sm border border-slate-700 mb-8">
          <h2 className="text-lg font-bold text-white mb-4">Classement final</h2>
          <div className="space-y-3">
            {rankings.map((player, idx) => {
              const style = RANK_STYLES[idx] || {
                bg: 'from-slate-700/20 to-slate-800/20',
                border: 'border-slate-600',
                icon: null,
                iconColor: 'text-slate-400',
              };
              const RankIcon = style.icon;
              const isYou = player.username === currentUsername;

              return (
                <div
                  key={player.username}
                  className={`flex items-center gap-4 p-4 rounded-xl border bg-gradient-to-r transition-all ${style.bg} ${style.border} ${
                    isYou ? 'ring-2 ring-blue-400/50' : ''
                  }`}
                >
                  {/* Rank */}
                  <div className="w-8 h-8 flex items-center justify-center">
                    {RankIcon ? (
                      <RankIcon className={`w-6 h-6 ${style.iconColor}`} />
                    ) : (
                      <span className="text-lg font-bold text-slate-400">#{idx + 1}</span>
                    )}
                  </div>

                  {/* Player info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold truncate ${isYou ? 'text-blue-300' : 'text-white'}`}>
                        {player.username}
                      </span>
                      {isYou && <span className="text-xs text-blue-400">(vous)</span>}
                    </div>
                    <span className="text-xs text-slate-400">
                      {player.country_short_name || '—'}
                    </span>
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    <div className="text-lg font-bold font-mono text-white">
                      {player.final_score?.toFixed(0) || '0'}
                    </div>
                    <div className="text-xs text-slate-400">pts</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/accueil')}
            className="flex-1 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Accueil
          </button>
          <button
            onClick={() => navigate('/multi')}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white font-bold transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            Nouvelle partie multi
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiGameOver;
